import { ApolloQueryResult, OperationVariables } from "@apollo/client";
import { AsyncSelect, ButtonAdd } from "@neonumy/ant-design";
import TooltipButton from "views/Documents/components/TooltipButton";
import { MdEdit } from "react-icons/md";
import useCategoryApi from "../hooks/useCategoryApi";
import { Col, Form, Input, Modal, Row } from "antd";
import useNotification from "utils/hooks/useNotification";
import { getFormError } from "utils/getFormError";
import { useTranslation } from "react-i18next";
const { TextArea } = Input;
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { FaSave } from "react-icons/fa";
import React from "react";
import useCustomQueryVariables from "views/Settings/Accounting/components/Sales/SalesAccounts/hooks/useCustomQueryVariables";
import { clientCentralApiStrawberry } from "@neonumy/api-clients";
import { LIST_COUNTERPARTS_QUERY } from "queries/centralApi/counterparts";
import useCoreStore from "views/hooks/useCoreStore";
import { createOptionCounterPartWithCycle } from "utils/createOptionCounterPartWithCycle";

interface CreateUpdateCategoryFormPropsTypes {
  refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
  id?: ID;
}

export default function CreateUpdateCategory(
  props: CreateUpdateCategoryFormPropsTypes,
): React.ReactElement {
  const { id, refetch } = props;
  const urlParams: RouteParams = useParams();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const companySettings = useCoreStore(state => state.companySettings);
  const counterpartProductAllocationType = companySettings?.counterpartProductAllocationType?.value;
  const counterpartByCategory = counterpartProductAllocationType === "by_category";
  const [isOpen, setIsOpen] = useState(false);

  const { createUpdateCategory, getCategory, formData } = useCategoryApi(id);
  const { notificationError, notificationSuccessfullySaved } = useNotification();
  const customQueryVariables = useCustomQueryVariables();

  const data = formData;

  useEffect(() => {
    if (id && isOpen) {
      getCategory({
        variables: {
          id,
        },
      });
    }
  }, [id, isOpen]);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        name: data.name,
        reference: data.reference,
        description: data.description,
        counterpartCode: {
          label: data?.counterpartCode,
          value: data?.counterpartCode,
        },
      });
    }
  }, [data]);

  const onFinish = async (values: Record<string, any>) => {
    let errors: any = [];
    const updateId = id ? { id } : { companyId: urlParams.companyId };

    const variables = {
      input: {
        name: values?.name,
        reference: values?.reference,
        description: values?.description,
        counterpartCode: values?.counterpartCode?.value,
        ...updateId,
      },
    };

    const response = await createUpdateCategory({ variables });

    errors = id ? response?.data?.updateCategory?.errors : response?.data?.createCategory?.errors;

    if (errors && errors.length > 0) {
      const fieldsData = getFormError(errors, values);
      notificationError(errors);
      form.setFields(fieldsData);
    } else {
      refetch();
      notificationSuccessfullySaved(t("Category"));
      form.resetFields();
      setIsOpen(false);
    }
  };

  const showModal = () => {
    setIsOpen(true);
  };

  const handleOk = () => {
    form.submit();
  };
  const handleCancel = () => {
    setIsOpen(false);
  };

  return (
    <>
      {id ? (
        <TooltipButton placement="top" handleOnClick={showModal} icon={<MdEdit />} />
      ) : (
        <Row justify="end" className="margin-top">
          <ButtonAdd onClick={showModal}>{t("Add")}</ButtonAdd>
        </Row>
      )}
      <Modal
        title={id ? t("Edit category") : t("New category")}
        open={isOpen}
        onOk={handleOk}
        okText={t("Save")}
        okButtonProps={{
          icon: (
            <span className="anticon">
              <FaSave />
            </span>
          ),
        }}
        onCancel={handleCancel}
        width={"30%"}
        className="antd-modal"
      >
        <Form
          name="createUpdateCategory"
          layout="vertical"
          form={form}
          onFinish={onFinish}
          autoComplete="off"
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                name="name"
                label={t("Name")}
                rules={[{ required: true, message: t("This field is required") }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="reference"
                label={t("Reference")}
                rules={[{ required: true, message: t("This field is required") }]}
              >
                <Input />
              </Form.Item>
            </Col>
            {counterpartByCategory && (
              <Col span={24}>
                <Form.Item
                  name="counterpartCode"
                  label={t("Counterpart product")}
                  rules={[{ required: true, message: t("This field is required") }]}
                >
                  <AsyncSelect
                    optionsFormat={createOptionCounterPartWithCycle}
                    customQueryVariables={customQueryVariables}
                    hasQueryFilter={true}
                    queryOptions={{
                      client: clientCentralApiStrawberry,
                      dataName: "listCounterpartsPaginated",
                      query: LIST_COUNTERPARTS_QUERY,
                      variables: {
                        filter: {
                          companyId: urlParams.companyId,
                          isActive: true,
                          search: "",
                          code_Istartswith_In: ["7"],
                        },
                        limit: 100,
                      },
                    }}
                    translations={{
                      placeholder: t(""),
                      loadingMessage: t("Loading..."),
                      noOptionsMessage: t("No Options"),
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            <Col span={24}>
              <Form.Item name="description" label={t("Description")}>
                <TextArea style={{ height: 150 }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
